import { useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert } from '@ds/feedback';
import type { ModeloDeDocumento } from '@/data/tipos';
import { ModeloModal } from '../documentos/ModeloModal';

/**
 * Modelos de atestados e prescrições.
 *
 * São modelos de documento das duas categorias — editados no mesmo
 * `ModeloModal` da tela de Documentos, com os mesmos campos dinâmicos. Modelo
 * sem texto não publica: atestado em branco não se emite.
 */
export function ModelosDeAtestadoAba({ modelos }: { modelos: ModeloDeDocumento[] }) {
    const revalidator = useRevalidator();
    const [alvo, setAlvo] = useState<ModeloDeDocumento | 'novo' | null>(null);
    const semTexto = modelos.filter((m) => !m.corpo.trim()).length;

    const colunas: DataTableColumn<ModeloDeDocumento>[] = [
        { key: 'nome', header: 'Nome', render: (m) => <strong style={{ fontWeight: 600 }}>{m.nome}</strong> },
        { key: 'categoria', header: 'Tipo', width: 130, render: (m) => <Badge tone={m.categoria === 'Atestado' ? 'info' : 'neutral'}>{m.categoria}</Badge> },
        { key: 'campos', header: 'Campos', align: 'right', width: 90 },
        {
            key: 'publicado', header: 'Situação', width: 140,
            render: (m) => (m.publicado
                ? <Badge tone="success">Publicado</Badge>
                : <Badge tone="neutral">{m.corpo.trim() ? 'Rascunho' : 'Sem texto'}</Badge>),
        },
        { key: 'acoes', header: '', width: 100, align: 'right', render: (m) => <Button size="sm" variant="secondary" onClick={() => setAlvo(m)}>Editar</Button> },
    ];

    return (
        <>
            {semTexto > 0 && (
                <Alert tone="warning" title={semTexto === 1 ? 'Um modelo ainda não tem texto' : `${semTexto} modelos ainda não têm texto`}>
                    Escreva o texto e publique para o modelo aparecer na emissão. Texto de atestado é da clínica: nenhum vem pronto.
                </Alert>
            )}
            <Card
                title="Modelos de atestados e prescrições" padding={0}
                subtitle="Os mesmos modelos da tela de Documentos, só destas duas categorias."
                actions={<Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setAlvo('novo')}>Novo modelo</Button>}
            >
                <DataTable
                    columns={colunas} rows={modelos} rowKey="id" stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhum modelo de atestado ou prescrição."
                />
            </Card>

            <ModeloModal
                alvo={alvo} categoriaInicial="Atestado"
                onClose={() => setAlvo(null)}
                onSalvo={() => { void revalidator.revalidate(); }}
            />
        </>
    );
}
